import { useState, useEffect } from 'react';
import { atom, useRecoilState } from 'recoil';
import { recoilPersist } from 'recoil-persist';
import { LocalCart } from '~/types/cart';

const { persistAtom } = recoilPersist();

const DEFAULT_CART_STATE = {
  count: 0,
  items: {
    lineItems: [],
  },
  timestamp: null, // Add a timestamp to the default state
  totalPrice: 0,
};

type CartState = {
  count: number;
  items: LocalCart;
  timestamp: string | null; // ISO string for the timestamp
  totalPrice: number;
};

export const cartState = atom<CartState>({
  key: 'cart',
  default: DEFAULT_CART_STATE,
  effects_UNSTABLE: [persistAtom],
});

export const useCartSSR = () => {
  const [value, setValue] = useRecoilState(cartState);
  const [isInitial, setIsInitial] = useState(true);

  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      if (value.timestamp) {
        const timeElapsed = Date.now() - new Date(value.timestamp).getTime();
        // 15 minutes in milliseconds
        if (timeElapsed > 15 * 60 * 1000) {
          // Reset the state to default if 15 minutes have passed
          setValue(DEFAULT_CART_STATE);
        }
      } else {
        // Reset to default if there's no timestamp
        setValue(DEFAULT_CART_STATE);
      }
    }, 15000); // Check every 15 seconds

    return () => clearInterval(cleanupInterval); // Clean up interval on component unmount
  }, [value, setValue]);

  useEffect(() => {
    setIsInitial(false);
  }, []);

  /**
   * `count` and `totalPrice` are DERIVED from `items.lineItems`, never taken from the
   * caller. Callers used to maintain `count` by hand at five separate sites, so it could
   * drift from the lines it counts; passing it now is harmless but ignored.
   *
   * The empty check is on LINE COUNT, never on price. It used to read
   * `if (totalPrice === 0)`, which erased any bag whose lines were all free — so a £0.00
   * product could not be added at all. Free items are a product requirement (BPES-237),
   * and `totalPrice === 0` is now a legal state.
   *
   * `Number.isFinite` is not belt-and-braces: the old expression ended in `|| 0`, and
   * `NaN` is falsy, so a persisted line with a non-numeric price used to collapse to 0 and
   * get wiped. That accidental corrupted-persist recovery is kept deliberately — without
   * it such a cart survives and renders a blank total.
   */
  const updateCart = (newCartState: Partial<Omit<CartState, 'timestamp'>>) => {
    const lineItems = newCartState?.items?.lineItems ?? [];

    const totalPrice = lineItems.reduce(
      (acc, curr) => acc + curr.price * curr.quantity,
      0,
    );
    const count = lineItems.reduce((acc, curr) => acc + curr.quantity, 0);

    if (!lineItems.length || !Number.isFinite(totalPrice)) {
      setValue(DEFAULT_CART_STATE);
      return;
    }

    // Functional form: `updateCart` closes over `value`, so two writes in one tick would
    // otherwise clobber each other.
    setValue((prev) => ({
      ...prev,
      ...newCartState,
      count,
      timestamp: new Date().toISOString(), // Update the timestamp whenever the state changes
      totalPrice,
    }));
  };

  return [isInitial ? DEFAULT_CART_STATE : value, updateCart] as const;
};
